import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FileCheck, Clock, ArrowRight, ArrowLeft, CheckCircle2, XCircle, AlertTriangle,
  Award, ClipboardList, Bookmark, Send, Flag, FileText, LogOut, Loader2,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

// =========================================================================
// LEARNER - My Exams
// =========================================================================
function LearnerCombinedReportButton() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      // Cache-buster + no-cache request headers so a shared PC never
      // replays a previous student's report from disk / SW / proxy.
      const { data } = await api.get(`/learner/all-exams-report.pdf`, {
        responseType: "blob",
        params: { _t: Date.now() },
        headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
      });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `my_exam_record_${Date.now()}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Combined report downloaded.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={download} disabled={busy}
      className="inline-flex items-center gap-1.5 rounded-md border-2 border-[#329112] bg-[#329112]/5 px-3 py-1.5 text-xs font-bold text-[#256b0d] hover:bg-[#329112]/10 disabled:opacity-60"
      data-testid="learner-combined-report"
      title="Download my combined exam record (all topics)">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
      Download my combined report
    </button>
  );
}

export function LearnerMyExamsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/learner/exams");
        setRows(data.data.exams);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);
  return (
    <DashboardLayout title="My Exams" breadcrumbs={["My Learning", "Exams"]}>
      <div className="space-y-4" data-testid="learner-my-exams">
        {!loading && rows.length > 0 && (
          <div className="flex justify-end">
            <LearnerCombinedReportButton />
          </div>
        )}
        {loading ? <LoadingBlock /> : rows.length === 0 ? (
          <EmptyState title="No exams assigned" description="Your admin will assign exams to you shortly." icon={FileCheck} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((row) => <ExamCard key={row.assignment.id} row={row} />)}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function ExamCard({ row }) {
  const e = row.exam;
  const attemptsUsed = row.attempts_used;
  const canAttempt = attemptsUsed < row.allowed_attempts;
  const latest = row.latest_attempt;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-md transition" data-testid={`learner-exam-${e.id}`}>
      <div className="h-24 bg-gradient-to-br from-[#101876] to-[#329112] p-4 text-white">
        <div className="text-xs opacity-80 font-bold uppercase tracking-widest">{e.exam_type?.replace(/_/g, " ")}</div>
        <div className="text-sm font-bold mt-1 line-clamp-2">{e.exam_title}</div>
      </div>
      <div className="p-4 space-y-2">
        <div className="text-xs text-slate-500 font-mono">{e.exam_code} · {row.course?.course_title}</div>
        <div className="grid grid-cols-3 gap-2 pt-1 text-xs">
          <div><b>{e.total_questions_to_display}</b> Qs</div>
          <div><b>{e.duration_minutes}m</b></div>
          <div><b>{e.passing_percentage}%</b></div>
        </div>
        <div className="text-xs text-slate-500">Attempts: <b>{attemptsUsed} / {row.allowed_attempts}</b></div>
        {latest && (
          <div className="text-xs">
            Last attempt: <StatusBadge status={latest.pass_fail_status || latest.status} /> · {latest.percentage}%
          </div>
        )}
      </div>
      <Link
        to={`/app/learner/exams/${row.assignment.id}`}
        className="border-t border-slate-100 bg-slate-50 px-4 py-2.5 text-xs font-bold text-[#101876] hover:bg-slate-100 flex items-center justify-between"
        data-testid={`learner-exam-open-${e.id}`}
      >
        <span>{canAttempt ? (attemptsUsed === 0 ? "Start exam" : "Continue / Reattempt") : "View history"}</span>
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

// =========================================================================
// LEARNER - Exam Instructions Page
// =========================================================================
export function LearnerExamInstructionsPage() {
  const { assignmentId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [declared, setDeclared] = useState(false);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await api.get(`/learner/exams/${assignmentId}`);
        setData(r.data);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [assignmentId]);

  const start = async () => {
    setStarting(true);
    try {
      const { data: r } = await api.post(`/learner/exams/${assignmentId}/start`);
      nav(`/app/learner/exam-attempts/${r.data.attempt_id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setStarting(false); }
  };

  if (!data) return <DashboardLayout title="Exam"><LoadingBlock /></DashboardLayout>;
  const e = data.exam;
  const canAttempt = data.attempts.length < data.allowed_attempts;
  const inProgress = data.attempts.find((a) => a.status === "in_progress");
  return (
    <DashboardLayout title={e.exam_title} breadcrumbs={["My Exams", e.exam_code]}>
      <div className="max-w-3xl space-y-4" data-testid="exam-instructions">
        <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="bg-gradient-to-br from-[#101876] to-[#329112] p-6 text-white">
            <div className="text-xs opacity-80 font-bold uppercase tracking-widest">{e.exam_type?.replace(/_/g, " ")}</div>
            <div className="text-xl font-bold mt-1">{e.exam_title}</div>
            <div className="text-xs opacity-80 mt-1">{data.course?.course_title}</div>
          </div>
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div className="rounded-md bg-slate-50 p-3"><div className="text-2xl font-bold">{e.total_questions_to_display}</div><div className="text-xs text-slate-500">Questions</div></div>
              <div className="rounded-md bg-slate-50 p-3"><div className="text-2xl font-bold">{e.duration_minutes}</div><div className="text-xs text-slate-500">Minutes</div></div>
              <div className="rounded-md bg-slate-50 p-3"><div className="text-2xl font-bold">{e.passing_percentage}%</div><div className="text-xs text-slate-500">Passing</div></div>
              <div className="rounded-md bg-slate-50 p-3"><div className="text-2xl font-bold">{data.attempts.length}/{data.allowed_attempts}</div><div className="text-xs text-slate-500">Attempts</div></div>
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-1">
              <div className="font-bold flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Instructions</div>
              <ul className="list-disc pl-6 space-y-1 text-xs">
                <li>The timer starts as soon as you begin the exam.</li>
                <li>{e.auto_submit_on_time_expiry ? "Exam will auto-submit when time expires." : "You must submit manually."}</li>
                <li>{e.randomize_questions ? "Questions are randomized for this attempt." : "Questions appear in fixed order."}</li>
                <li>{e.randomize_options ? "Options are shuffled for each question." : "Options appear in fixed order."}</li>
                <li>{e.allow_back_navigation ? "You can navigate back to previous questions." : "You cannot go back."}</li>
                <li>{e.negative_marking_enabled ? "Negative marking is enabled." : "No negative marking."}</li>
                <li>Copy/paste is {e.copy_paste_disabled ? "disabled" : "enabled"} during this exam.</li>
                <li>Tab switching is logged as a security event.</li>
              </ul>
            </div>
            {data.attempts.length > 0 && (
              <div className="rounded-md border border-slate-200 p-3">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Previous attempts</div>
                <div className="space-y-1">
                  {data.attempts.map((a) => (
                    <div key={a.id} className="flex items-center justify-between text-xs" data-testid={`prev-attempt-${a.id}`}>
                      <div>#{a.attempt_number} · {a.submitted_at?.slice(0, 10) || "in progress"}</div>
                      <div className="flex items-center gap-2">
                        <span>{a.percentage ?? 0}%</span>
                        <StatusBadge status={a.pass_fail_status || a.status} />
                        {(a.status === "passed" || a.status === "failed") && (
                          <>
                            <Link to={`/app/learner/exam-results/${a.id}`} className="text-[#101876] font-bold hover:underline">View</Link>
                            <button
                              onClick={async () => {
                                try {
                                  const { data: pdf } = await api.get(`/learner/exam-attempts/${a.id}/summary-report.pdf`, { responseType: "blob" });
                                  const url = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }));
                                  const link = document.createElement("a");
                                  link.href = url;
                                  link.download = `report_${e.exam_code}_attempt${a.attempt_number}.pdf`;
                                  link.click();
                                  URL.revokeObjectURL(url);
                                } catch (err) { toast.error(formatApiError(err)); }
                              }}
                              className="inline-flex items-center gap-1 rounded-md bg-[#329112] px-2 py-0.5 text-[10px] font-bold text-white hover:bg-[#256b0d]"
                              data-testid={`prev-attempt-report-${a.id}`}
                              title="Download report card (PDF)"
                            >
                              <FileText className="h-3 w-3" /> Report
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={declared} onChange={(e) => setDeclared(e.target.checked)} className="mt-1" data-testid="exam-declaration" />
              <span>I confirm that I am attempting this exam myself, and I will not use any unfair means during this assessment.</span>
            </label>
            <div className="flex justify-end">
              {inProgress ? (
                <button onClick={() => nav(`/app/learner/exam-attempts/${inProgress.id}`)}
                  className="rounded-md bg-emerald-600 px-6 py-2.5 text-sm font-bold text-white hover:bg-emerald-700" data-testid="btn-resume-exam">
                  Resume exam →
                </button>
              ) : (
                <button disabled={!declared || !canAttempt || starting} onClick={start}
                  className="rounded-md bg-[#101876] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#0c135c] disabled:opacity-40" data-testid="btn-start-exam">
                  {starting ? "Starting…" : canAttempt ? "Start exam" : "Max attempts used"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// LEARNER - Online Exam Attempt Interface
// =========================================================================
export function LearnerExamAttemptPage() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});  // question_id -> { selected, descriptive, marked }
  const [remaining, setRemaining] = useState(0);
  const [current, setCurrent] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const timerRef = useRef(null);
  const tabSwitchesRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/learner/exam-attempts/${attemptId}`);
      setAttempt(data.data.attempt);
      setExam(data.data.exam);
      setQuestions(data.data.questions);
      setRemaining(data.data.remaining_seconds);
      const initial = {};
      data.data.questions.forEach((q) => {
        initial[q.question_id] = {
          selected: q.selected_option_ids || [],
          descriptive: q.descriptive_answer || "",
          marked: q.marked_for_review || false,
        };
      });
      setAnswers(initial);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [attemptId]);
  useEffect(() => { load(); }, [load]);

  // Timer
  useEffect(() => {
    if (remaining <= 0) return;
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(timerRef.current);
          autoSubmit();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [attempt?.id]);

  // Tab switch detection
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden && attempt?.status === "in_progress") {
        tabSwitchesRef.current += 1;
        api.post(`/learner/exam-attempts/${attemptId}/security-log`, {
          event_type: "tab_switch",
          event_description: `Tab switch #${tabSwitchesRef.current}`,
        }).catch(() => {});
        toast.warning(`Tab switch detected (${tabSwitchesRef.current})`);
      }
    };
    const preventCopy = (e) => {
      if (exam?.copy_paste_disabled) e.preventDefault();
    };
    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("copy", preventCopy);
    document.addEventListener("paste", preventCopy);
    document.addEventListener("contextmenu", preventCopy);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("copy", preventCopy);
      document.removeEventListener("paste", preventCopy);
      document.removeEventListener("contextmenu", preventCopy);
    };
  }, [exam, attempt, attemptId]);

  const saveAnswer = useCallback(async (qid, patch) => {
    setAnswers((p) => ({ ...p, [qid]: { ...p[qid], ...patch } }));
    try {
      await api.post(`/learner/exam-attempts/${attemptId}/save-answer`, {
        question_id: qid,
        selected_option_ids: patch.selected ?? answers[qid]?.selected ?? [],
        descriptive_answer: patch.descriptive ?? answers[qid]?.descriptive ?? "",
        marked_for_review: patch.marked ?? answers[qid]?.marked ?? false,
      });
    } catch { /* silent */ }
  }, [answers, attemptId]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/learner/exam-attempts/${attemptId}/submit`);
      toast.success("Exam submitted.");
      nav(`/app/learner/exam-results/${data.data.attempt.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); setShowSubmitDialog(false); }
  };

  const autoSubmit = async () => {
    try {
      const { data } = await api.post(`/learner/exam-attempts/${attemptId}/submit`);
      toast.info("Time up! Exam auto-submitted.");
      nav(`/app/learner/exam-results/${data.data.attempt.id}`);
    } catch { /* */ }
  };

  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  if (!attempt || !exam) return <div className="min-h-screen flex items-center justify-center"><LoadingBlock /></div>;

  const q = questions[current];
  if (!q) return null;
  const ans = answers[q.question_id] || { selected: [], descriptive: "", marked: false };
  const answeredCount = Object.values(answers).filter((a) => (a.selected?.length > 0) || (a.descriptive?.trim())).length;
  const isObjective = ["single_choice", "multi_select", "true_false"].includes(q.question_type);
  const timeCritical = remaining < 300; // <5min

  return (
    <div className="min-h-screen bg-slate-50 select-none" data-testid="exam-attempt">
      {/* Top bar */}
      <div className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => setShowExitDialog(true)}
            className="rounded-md border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1"
            data-testid="btn-exit-exam" title="Save & exit — answers are auto-saved">
            <ArrowLeft className="h-3.5 w-3.5" /> Exit
          </button>
          <div className="flex-1">
            <div className="text-sm font-bold text-slate-800 line-clamp-1">{exam.exam_title}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{exam.exam_code} · Attempt #{attempt.attempt_number}</div>
          </div>
          <div className={`rounded-md px-4 py-2 font-mono text-sm font-bold ${timeCritical ? "bg-rose-100 text-rose-700 animate-pulse" : "bg-slate-100 text-slate-700"}`} data-testid="exam-timer">
            <Clock className="inline h-3.5 w-3.5 mr-1" /> {fmtTime(remaining)}
          </div>
          <button onClick={() => setShowSubmitDialog(true)} className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c] flex items-center gap-1" data-testid="btn-submit-exam">
            <Send className="h-3.5 w-3.5" /> Submit
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 grid gap-4 lg:grid-cols-[1fr_260px]">
        {/* Question area */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5 min-h-[400px]" data-testid="question-panel">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
              Question {current + 1} of {questions.length} · {q.marks} mark{q.marks > 1 ? "s" : ""}
            </div>
            <button onClick={() => saveAnswer(q.question_id, { marked: !ans.marked })}
              className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-bold ${ans.marked ? "bg-amber-100 text-amber-800" : "border border-slate-200 text-slate-700 hover:bg-slate-50"}`}
              data-testid="btn-mark-review">
              <Flag className="h-3.5 w-3.5" /> {ans.marked ? "Marked for review" : "Mark for review"}
            </button>
          </div>
          {q.case_scenario && <div className="rounded-md bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-line">{q.case_scenario}</div>}
          <div className="text-base font-semibold text-slate-800 whitespace-pre-line">{q.question_text}</div>
          {isObjective && (
            <div className="space-y-2" data-testid="options-list">
              {q.options.map((opt) => {
                const checked = ans.selected.includes(opt.id);
                const inputType = q.question_type === "multi_select" ? "checkbox" : "radio";
                return (
                  <label key={opt.id} className={`flex items-start gap-2 rounded-md border p-3 text-sm cursor-pointer transition ${checked ? "border-[#329112] bg-emerald-50/60" : "border-slate-200 bg-white hover:bg-slate-50"}`} data-testid={`option-${opt.id}`}>
                    <input type={inputType} name={q.question_id} checked={checked}
                      onChange={() => {
                        let sel;
                        if (q.question_type === "multi_select") {
                          sel = checked ? ans.selected.filter((x) => x !== opt.id) : [...ans.selected, opt.id];
                        } else {
                          sel = [opt.id];
                        }
                        saveAnswer(q.question_id, { selected: sel });
                      }}
                      className="mt-0.5" />
                    <span>{opt.option_text}</span>
                  </label>
                );
              })}
            </div>
          )}
          {!isObjective && (
            <textarea rows={8} value={ans.descriptive} onChange={(e) => saveAnswer(q.question_id, { descriptive: e.target.value })}
              placeholder="Type your answer here…"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="descriptive-textarea" />
          )}
          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <button disabled={!exam.allow_back_navigation || current === 0} onClick={() => setCurrent((c) => c - 1)}
              className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1" data-testid="btn-prev">
              <ArrowLeft className="h-3.5 w-3.5" /> Previous
            </button>
            <button onClick={() => saveAnswer(q.question_id, { selected: [], descriptive: "" })}
              className="text-xs text-rose-600 hover:underline" data-testid="btn-clear">Clear response</button>
            <button disabled={current >= questions.length - 1} onClick={() => setCurrent((c) => c + 1)}
              className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c] disabled:opacity-40 flex items-center gap-1" data-testid="btn-next">
              Save & Next <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Question palette */}
        <aside className="rounded-2xl border border-slate-200 bg-white p-4 h-fit" data-testid="palette">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Question palette</div>
          <div className="grid grid-cols-5 gap-2">
            {questions.map((qq, i) => {
              const a = answers[qq.question_id];
              const isAns = (a?.selected?.length > 0) || (a?.descriptive?.trim());
              const isMark = a?.marked;
              let cls = "border-slate-200 bg-white text-slate-500";
              if (isMark) cls = "border-amber-300 bg-amber-100 text-amber-800";
              else if (isAns) cls = "border-emerald-300 bg-emerald-100 text-emerald-800";
              if (i === current) cls += " ring-2 ring-[#101876] ring-offset-1";
              return (
                <button key={qq.question_id} onClick={() => setCurrent(i)} className={`h-9 rounded-md border text-xs font-bold ${cls}`} data-testid={`palette-${i + 1}`}>
                  {i + 1}
                </button>
              );
            })}
          </div>
          <div className="mt-4 space-y-1 text-[10px] text-slate-500">
            <div className="flex items-center gap-2"><span className="inline-block h-3 w-3 rounded bg-emerald-100 border border-emerald-300" /> Answered</div>
            <div className="flex items-center gap-2"><span className="inline-block h-3 w-3 rounded bg-amber-100 border border-amber-300" /> Marked for review</div>
            <div className="flex items-center gap-2"><span className="inline-block h-3 w-3 rounded bg-white border border-slate-300" /> Not answered</div>
          </div>
          <div className="mt-4 border-t border-slate-100 pt-3 text-xs">
            <div>Answered: <b>{answeredCount}</b> / {questions.length}</div>
          </div>
        </aside>
      </div>

      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Submit exam?</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            <div>You have answered <b>{answeredCount}</b> of <b>{questions.length}</b> questions.</div>
            <div>Once submitted, you cannot change your answers.</div>
          </div>
          <DialogFooter>
            <button onClick={() => setShowSubmitDialog(false)} className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold hover:bg-slate-50">Continue exam</button>
            <button disabled={submitting} onClick={submit} className="rounded-md bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700" data-testid="confirm-submit">
              {submitting ? "Submitting…" : "Submit now"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Exit exam?</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            <div>Your answers are <b>auto-saved</b> — you can resume this attempt from the exam list.</div>
            <div>The timer will keep running. If it expires before you return, the attempt will be auto-submitted with the answers saved so far.</div>
          </div>
          <DialogFooter>
            <button onClick={() => setShowExitDialog(false)} className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold hover:bg-slate-50" data-testid="exit-cancel">Continue exam</button>
            <button onClick={() => nav(-1)} className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c] flex items-center gap-1" data-testid="exit-confirm">
              <LogOut className="h-3.5 w-3.5" /> Exit & save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================================================
// LEARNER - Exam Result
// =========================================================================
export function LearnerExamResultPage() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await api.get(`/learner/exam-attempts/${attemptId}/result`);
        setData(r.data);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [attemptId]);

  if (!data) return <DashboardLayout title="Result"><LoadingBlock /></DashboardLayout>;
  const a = data.attempt;
  const e = data.exam;
  const passed = a.pass_fail_status === "passed";
  const isUnder = a.hide_result || a.result_status === "under_review";

  return (
    <DashboardLayout title="Exam result" breadcrumbs={["My Exams", "Result"]}>
      <div className="max-w-3xl space-y-4" data-testid="exam-result">
        <button onClick={() => nav("/app/learner/exams")} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back to exams</button>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className={`p-8 text-center text-white ${isUnder ? "bg-gradient-to-br from-slate-500 to-slate-600" : passed ? "bg-gradient-to-br from-emerald-500 to-emerald-700" : "bg-gradient-to-br from-rose-500 to-rose-700"}`}>
            {isUnder ? (
              <>
                <Clock className="h-12 w-12 mx-auto mb-3 opacity-90" />
                <div className="text-2xl font-bold">Under Review</div>
                <div className="text-sm opacity-90 mt-1">Descriptive answers awaiting evaluation.</div>
              </>
            ) : (
              <>
                {passed ? <Award className="h-12 w-12 mx-auto mb-3 opacity-90" /> : <XCircle className="h-12 w-12 mx-auto mb-3 opacity-90" />}
                <div className="text-3xl font-bold">{passed ? "Passed" : "Failed"}</div>
                <div className="text-4xl font-black mt-2">{a.percentage}%</div>
                <div className="text-sm opacity-90 mt-1">{a.marks_obtained} / {a.total_marks} marks</div>
              </>
            )}
          </div>
          <div className="p-6 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div><div className="text-2xl font-bold">{a.attempted_questions}</div><div className="text-xs text-slate-500">Attempted</div></div>
            <div><div className="text-2xl font-bold text-emerald-700">{a.correct_answers}</div><div className="text-xs text-slate-500">Correct</div></div>
            <div><div className="text-2xl font-bold text-rose-700">{a.wrong_answers}</div><div className="text-xs text-slate-500">Wrong</div></div>
            <div><div className="text-2xl font-bold text-slate-500">{a.unanswered_questions}</div><div className="text-xs text-slate-500">Unanswered</div></div>
          </div>
          <div className="border-t border-slate-100 p-6 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Exam</span><span className="font-medium">{e.exam_title}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Attempt #</span><span className="font-medium">{a.attempt_number}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Passing %</span><span className="font-medium">{e.passing_percentage}%</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Time taken</span><span className="font-medium">{Math.floor((a.time_taken_seconds || 0) / 60)}m {(a.time_taken_seconds || 0) % 60}s</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Submitted at</span><span className="font-medium">{a.submitted_at?.slice(0, 16).replace("T", " ")}</span></div>
          </div>
          <div className="border-t border-slate-100 p-4 flex flex-wrap gap-2 justify-end">
            {e.answer_review_allowed && !isUnder && (
              <Link to={`/app/learner/exam-review/${attemptId}`} className="rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50" data-testid="btn-review-answers">
                Review answers
              </Link>
            )}
            {e.allow_post_test_pdf_download !== false && !isUnder && (
              <>
                <button onClick={async () => {
                  try {
                    const { data } = await api.get(`/learner/exam-attempts/${attemptId}/summary-report.pdf`, { responseType: "blob" });
                    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `report_${e.exam_code}_${a.attempt_number}.pdf`;
                    link.click();
                    URL.revokeObjectURL(url);
                  } catch (err) { toast.error(formatApiError(err)); }
                }} className="rounded-md bg-[#329112] px-4 py-2 text-xs font-bold text-white hover:bg-[#256b0d] flex items-center gap-1" data-testid="btn-download-summary-report">
                  <FileText className="h-3.5 w-3.5" /> Download report card (PDF)
                </button>
                <button onClick={async () => {
                  try {
                    const { data } = await api.get(`/learner/exam-attempts/${attemptId}/pdf`, { responseType: "blob" });
                    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `attempt_${a.attempt_number}_${e.exam_code}.pdf`;
                    link.click();
                    URL.revokeObjectURL(url);
                  } catch (err) { toast.error(formatApiError(err)); }
                }} className="rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1" data-testid="btn-download-pdf">
                  <FileText className="h-3.5 w-3.5" /> Detailed Q&amp;A (PDF)
                </button>
              </>
            )}
            <Link to="/app/learner/exams" className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c]">
              Back to my exams
            </Link>
          </div>
        </div>
        {passed && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            <Award className="inline h-4 w-4 mr-1" /> Certificate eligibility recorded. Your certificate will be available in Phase 8 when the certificate module ships.
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// LEARNER - Answer Review
// =========================================================================
export function LearnerAnswerReviewPage() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await api.get(`/learner/exam-attempts/${attemptId}/review`);
        setData(r.data);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [attemptId]);
  if (!data) return <DashboardLayout title="Answer review"><LoadingBlock /></DashboardLayout>;

  return (
    <DashboardLayout title="Answer review" breadcrumbs={["My Exams", "Review"]}>
      <div className="max-w-3xl space-y-4" data-testid="answer-review">
        <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
        {data.review.map((r) => {
          const correctIds = new Set(r.correct_option_ids || []);
          const selectedIds = new Set(r.selected_option_ids || []);
          return (
            <div key={r.question_order} className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3" data-testid={`review-q-${r.question_order}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Q{r.question_order}</div>
                <div className="flex items-center gap-2">
                  {r.is_correct ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-bold"><CheckCircle2 className="h-3.5 w-3.5" /> Correct</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-bold"><XCircle className="h-3.5 w-3.5" /> Wrong</span>
                  )}
                  <span className="text-xs text-slate-500">+{r.marks_awarded}</span>
                </div>
              </div>
              <div className="text-sm font-semibold whitespace-pre-line">{r.question_text}</div>
              {r.options?.length > 0 && (
                <div className="space-y-1.5">
                  {r.options.map((o) => {
                    const isSel = selectedIds.has(o.id);
                    const isCorr = correctIds.has(o.id);
                    let cls = "border-slate-200 bg-white";
                    if (isCorr) cls = "border-emerald-400 bg-emerald-50";
                    else if (isSel) cls = "border-rose-300 bg-rose-50";
                    return (
                      <div key={o.id} className={`rounded-md border p-2 text-sm flex items-start gap-2 ${cls}`}>
                        {isCorr ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> : isSel ? <XCircle className="h-4 w-4 text-rose-600 mt-0.5" /> : <span className="mt-1 h-3.5 w-3.5 rounded-full border-2 border-slate-300" />}
                        <span>{o.option_text}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {r.descriptive_answer && (
                <div className="rounded-md bg-slate-50 p-3 text-sm">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Your answer</div>
                  <div className="whitespace-pre-line">{r.descriptive_answer}</div>
                </div>
              )}
              {r.explanation && (
                <div className="rounded-md border-l-2 border-[#329112] bg-emerald-50/40 pl-3 py-2 text-sm">
                  <div className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-1">Explanation</div>
                  <div className="whitespace-pre-line">{r.explanation}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
